/**
 * Clerk decision logic — pure, so it's unit-testable without the component-test
 * infrastructure this repo deliberately doesn't have (CLAUDE.md).
 *
 * READ THIS BEFORE TOUCHING ANYTHING CLERK-RELATED:
 *
 * Clerk authenticates a user *to Bosun*. It does NOT authorise a single Frappe API call.
 * Every `/api/method/quoflow.*` request is authorised by Frappe's own `sid` session cookie,
 * and role gates (purchase_manager reassigning a purchaser, deciding won/lost) are enforced
 * server-side against the FRAPPE user, not the Clerk user. A signed-in Clerk session carries
 * no weight with Frappe whatsoever.
 *
 * So this file (and `ClerkIdentityBridge.tsx`, `SessionProvider.tsx`) builds a SIGN-IN WALL
 * and an IDENTITY SOURCE for Bosun's own UI — e.g. "who is this browser tab as, for a Slack
 * mention or an audit log". The bridge itself lives elsewhere: `FrappeSessionGate` (mounted
 * above `FrappeProvider` in main.tsx) runs at boot, calls `frappeSession.ts`'s
 * `ensureFrappeSession()`, which POSTs a fresh Clerk-signed JWT to `auth.session_from_clerk`
 * and — once Frappe verifies it — gets back a `sid` cookie; `sessionRetry.ts` re-runs that
 * same exchange once if a later call reports the session expired. None of that changes the
 * paragraph above: role gates are still enforced server-side against the FRAPPE user, and a
 * signed-in Clerk session still carries no authority of its own — the exchange is what lets a
 * Clerk identity obtain a Frappe session, not a replacement for having one.
 *
 * The bridge is NOT inert until F1 (CORS) and F2 (SameSite=None cookie) land on the Frappe side —
 * an earlier version of this comment said it was, and that was wrong in a way that matters: with a
 * Clerk key configured but F1/F2 unlanded, `FrappeSessionGate` mounts, calls
 * `ensureFrappeSession()`, and that call fails (no CORS allow, or a `sid` the browser silently
 * drops) — the gate then renders its "Can't reach the Bosun server" screen and the app never
 * renders. That is a HARD BLOCK, not graceful degradation; "inert" would mean nothing happens,
 * which is the opposite of what's observed. This bites hardest in the specific combination of
 * `BOSUN_LIVE_URL` set (so `vite.config.ts` proxies `/api` to live) plus a Clerk key configured —
 * every call in that mode is already authorised by the proxy-injected API token, so the gate adds
 * no security and only blocks. `resolveSessionBridgeEnabled` below is the documented,
 * explicit-only escape hatch (`VITE_BOSUN_SESSION_BRIDGE=0`) for exactly that combination — see
 * its own comment for why it must stay explicit rather than becoming automatic. Tracked as E1 in
 * docs/bosun-go-live-checklist.md. The keyless dev mode (no `VITE_CLERK_PUBLISHABLE_KEY`) still
 * bypasses all of it, Clerk included, regardless of this flag.
 */

/** Which provider tree `main.tsx` mounts. Derived once from the env var at startup — this is
 * NOT meant to change within a running app; there is no "log out of Clerk, fall back to the
 * stub" transition. */
export type ClerkAuthMode = 'clerk' | 'stub'

/** Console warning shown exactly once, at startup, when no key is configured. Exported so the
 * test can assert on its content without duplicating the string. */
export const CLERK_NOT_CONFIGURED_WARNING =
  '[bosun] VITE_CLERK_PUBLISHABLE_KEY is not set — running with the SessionProvider stub ' +
  '(no real sign-in, anyone reaching the app is "signed in"). This is expected in local dev ' +
  'without a Clerk key; set VITE_CLERK_PUBLISHABLE_KEY to exercise the real sign-in wall.'

/**
 * Who signs people in on THIS deployment.
 *
 * `frappe` (Akhil, 2026-09-24) is an instance with Clerk switched off: the app is served by the
 * Frappe site itself, people sign in with their Frappe username and password, and the ordinary
 * `sid` session authorises every call — same origin, no proxy, no API token, each request running
 * as the person who made it. `clerk` is what every deployment does today, and `stub` is keyless
 * local dev.
 *
 * A DEPLOY's choice, read once at startup: one instance, one sign-in method. Anything other than
 * an explicit `frappe` keeps the Clerk wall — an unrecognised value must never quietly remove it.
 */
export type AuthProvider = 'clerk' | 'frappe' | 'stub'

export function resolveAuthProvider(setting: string | undefined, publishableKey: string | undefined): AuthProvider {
  if ((setting ?? '').trim().toLowerCase() === 'frappe') return 'frappe'
  return resolveClerkAuthMode(publishableKey)
}

/**
 * Decide whether to mount the real Clerk provider tree or fall back to today's stub. Pure: no
 * env reads, no console, no React — callers pass `import.meta.env.VITE_CLERK_PUBLISHABLE_KEY`
 * in and get a mode out, so this is testable without touching Vite's env plumbing.
 *
 * A blank/whitespace-only value counts as unset — `.env` files sometimes have a key present
 * with an empty value, and that must degrade the same way as the var being absent entirely,
 * not crash ClerkProvider with an empty publishableKey.
 */
export function resolveClerkAuthMode(publishableKey: string | undefined): ClerkAuthMode {
  return publishableKey && publishableKey.trim().length > 0 ? 'clerk' : 'stub'
}

/**
 * Whether `main.tsx` mounts `FrappeSessionGate` at all, given a Clerk key is configured (when no
 * key is configured, `resolveClerkAuthMode` above already skips the whole Clerk tree and this
 * question never arises).
 *
 * Added for finding 4 of the 2026-08-08 whole-branch review: `BOSUN_LIVE_URL` set (live Frappe)
 * plus a Clerk key together used to work — every call was already authorised by the Vite proxy's
 * injected API token — and this bridge's gate BROKE that combination, because
 * `auth.session_from_clerk` does not exist on live Frappe yet, so the gate's exchange fails and the
 * app never renders (see this file's header for why that is a hard block, not graceful
 * degradation).
 *
 * The fix is an EXPLICIT opt-out, `VITE_BOSUN_SESSION_BRIDGE=0` — deliberately not an automatic
 * one keyed off `BOSUN_LIVE_URL` or anything else. Automatic would be wrong the moment F1/F2/F5
 * land on the Frappe side, because at that point the session bridge becomes the ONLY thing that
 * authorises a live call from a real user's browser (the proxy-injected token is a DEV-ONLY
 * mechanism per the design spec's C1 — it cannot ship to production, where there is no server
 * process of ours to inject it from). An automatic skip would silently keep working right up until
 * someone removed the dev-only token path, at which point every request would 401 with no gate to
 * explain why. Requiring a person to type this flag keeps that choice visible.
 *
 * Pure and unit-tested, matching `resolveClerkAuthMode`'s pattern, rather than reading
 * `import.meta.env` inline in `FrappeSessionGate`'s mount site.
 */
export function resolveSessionBridgeEnabled(override: string | undefined): boolean {
  return override !== '0'
}

/** The signed-in-to-Bosun identity, once derived. Deliberately just email + display name — see
 * this file's header. Never a role: role stays sourced from `SessionProvider`'s own stub state
 * until Y3 (the real Frappe role names) is answered, so there is nothing here to map. */
export interface ClerkIdentity {
  email: string
  name: string
}

/**
 * The slice of Clerk's `UserResource` this app actually reads. Kept narrow and structural
 * (rather than importing Clerk's own type) so `deriveClerkIdentity` stays a plain, dependency-
 * free function callers can unit-test with a plain object literal — no Clerk SDK, no mocking
 * `@clerk/clerk-react`, which this repo has no infrastructure for anyway.
 */
export interface ClerkUserLike {
  primaryEmailAddress?: { emailAddress: string } | null
  fullName?: string | null
  username?: string | null
}

/**
 * Turn Clerk's user object into the plain identity the rest of the app cares about. Pure —
 * `ClerkIdentityBridge.tsx` is the only caller that touches the actual `useUser()` hook; this
 * function is what's unit-tested.
 *
 * Returns `null` when there's no usable email — Clerk allows sign-in methods (e.g. some SSO
 * connections, phone-only accounts) that never populate `primaryEmailAddress`, and email is the
 * one field callers actually rely on identifying a person by (mail filtering, audit trails), so
 * a nameless-but-emailless "identity" is not useful enough to hand back.
 */
export function deriveClerkIdentity(user: ClerkUserLike | null | undefined): ClerkIdentity | null {
  if (!user) return null
  const email = user.primaryEmailAddress?.emailAddress?.trim()
  if (!email) return null
  const name = user.fullName?.trim() || user.username?.trim() || email
  return { email, name }
}

/**
 * Whether to open the realtime socket.
 *
 * Extracted and unit-tested because the inline version shipped a silent bug: `vite.config.ts`
 * injects `VITE_LIVE_BACKEND` via `define`, and `JSON.stringify(Boolean(LIVE))` inlines the
 * BOOLEAN literal `true` — while the comparison read `=== 'true'`, a string. `true === 'true'`
 * is false, so the socket was never enabled and every realtime-dependent feature (AI extraction
 * especially, whose result arrives ONLY on `extract.done`) failed with "not connected".
 *
 * Both inputs are therefore accepted as either a boolean or its string spelling — `define` and
 * `.env` disagree about which they produce, and a type mismatch here is invisible until a
 * feature silently stops working.
 */
export function resolveEnableSocket(
  override: unknown,
  liveBackend: unknown,
): boolean {
  const truthy = (v: unknown): boolean | undefined =>
    v === true || v === 'true' ? true : v === false || v === 'false' ? false : undefined
  return truthy(override) ?? truthy(liveBackend) ?? false
}
